import ModalEvents from '@/enums/modalEvents';
import EventBus from '@/eventBus';
import Room from '@/models/room/room';
import hadrianModal from '@/shared/viewParts/hadrianModal/hadrianModal.vue';
import minimalRoomList from '@/shared/viewParts/minimalRoomList/minimalRoomList.vue';
import { vxManager } from '@/store/store';
import { defineComponent } from 'vue';

export interface ApplySettingsToRoomsModalData {
  readonly projectId: string;
  readonly selectedRoomIds?: string[];
  readonly onConfirm: (roomIds: string[], applyToAllRooms: boolean) => void;
  readonly onCancel?: () => void;
}

export default defineComponent({
  name: 'applySettingsToRoomsModal',
  components: {
    hadrianModal,
    minimalRoomList,
  },
  data() {
    return {
      projectId: '',
      selectedRoomIds: [] as string[],
      isLoadingRooms: false,
      onConfirm: undefined as ((roomIds: string[], applyToAllRooms: boolean) => void) | undefined,
      onCancel: undefined as (() => void) | undefined,
    };
  },
  created() {
    EventBus.$on(ModalEvents.ApplySettingsToRooms, this.show);
  },
  beforeUnmount() {
    EventBus.$off(ModalEvents.ApplySettingsToRooms, this.show);
  },
  computed: {
    rooms(): Room[] {
      return vxManager.roomsModule.getRoomsForProject(this.projectId);
    },
    areAllRoomsSelected(): boolean {
      const selectableRooms = this.rooms.filter(room => this.isSelectable(room));
      return selectableRooms.length > 0 && this.selectedRoomIds.length === selectableRooms.length;
    },
  },
  methods: {
    async show(data: ApplySettingsToRoomsModalData) {
      this.projectId = data.projectId;
      this.onConfirm = data.onConfirm;
      this.onCancel = data.onCancel;

      if (this.rooms.length === 0) {
        this.isLoadingRooms = true;
        await vxManager.roomsModule.fetchAllRoomsForProject(this.projectId);
        this.isLoadingRooms = false;
      }

      this.selectedRoomIds =
        data.selectedRoomIds ?? this.rooms.filter(room => this.isSelectable(room)).map(room => room.id);
      (this.$refs.applySettingsToRoomsModal as any).show();
    },
    isRoomSelected(roomId: string): boolean {
      return this.selectedRoomIds.includes(roomId);
    },
    isRoomOrdered(room: Room): boolean {
      return !!room.orderInformation_IsOrdered;
    },
    isSelectable(room: Room): boolean {
      return !this.isRoomOrdered(room);
    },
    toggleAllRooms(): void {
      this.selectedRoomIds = this.areAllRoomsSelected
        ? []
        : this.rooms.filter(room => this.isSelectable(room)).map(room => room.id);
    },
    onRoomSelect(room: Room): void {
      if (!this.isSelectable(room)) {
        return;
      }
      const index = this.selectedRoomIds.indexOf(room.id);
      if (index === -1) {
        this.selectedRoomIds.push(room.id);
      } else {
        this.selectedRoomIds.splice(index, 1);
      }
    },
    confirm() {
      if (this.onConfirm) {
        const applyToAllRooms = this.rooms.length > 0 && this.selectedRoomIds.length === this.rooms.length;
        this.onConfirm(this.selectedRoomIds, applyToAllRooms);
      }
      this.clean();
    },
    cancel() {
      if (this.onCancel) {
        this.onCancel();
      }
      this.clean();
      this.$emit('close');
    },
    clean() {
      this.projectId = '';
      this.selectedRoomIds = [];
      this.isLoadingRooms = false;
      this.onConfirm = undefined;
      this.onCancel = undefined;
    },
  },
});
